import api from "@/lib/axiosConfig";
import { Trash } from "@/lib/types";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

interface GetTrashData {
  where: any;
  orderBy: any;
  take: number;
  skip: number;
}

async function getTrash(data: GetTrashData) {
  const response = await api.post("/back/api/trash/get", data);
  return response.data;
}

export const useGetTrash = ({ where, orderBy, take, skip }: GetTrashData) => {
  return useQuery({
    queryKey: ["trash", { where, orderBy, take, skip }],
    queryFn: () => getTrash({ where, orderBy, take, skip }),
    placeholderData: keepPreviousData,
  });
};

async function addToTrash(data: Trash) {
  const response = await api.post("/back/api/trash", {
    data: data,
  });
  return response.data;
}

function invalidateTrashQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  documentType: string,
  documentIds: string[],
) {
  const listKeys: Record<string, string> = {
    artwork: "artworksDashboard",
    photography: "photographies",
    multipleprint: "multipleprints",
    author: "authors",
    bibliography: "bibliographies",
    writing: "writings",
    exhibition: "exhibitions",
    document: "documents",
    collector: "collectors",
    audiovisual: "audiovisuals",
    auction: "auctions",
    tirage: "tirages",
  };

  queryClient.invalidateQueries({ queryKey: ["trash"] });
  queryClient.invalidateQueries({
    queryKey: ["countByStatus", documentType],
    exact: false,
  });

  const listKey = listKeys[documentType];
  if (listKey) {
    queryClient.invalidateQueries({ queryKey: [listKey], exact: false });
  }

  for (const documentId of documentIds) {
    if (documentType === "artwork") {
      queryClient.invalidateQueries({ queryKey: ["artworks", documentId] });
    } else if (documentType === "tirage") {
      queryClient.invalidateQueries({ queryKey: ["tirage", documentId] });
    } else {
      queryClient.invalidateQueries({
        queryKey: [documentType, documentId],
      });
    }
  }

  if (documentType === "tirage") {
    queryClient.invalidateQueries({ queryKey: ["tirages"], exact: false });
  }
}

export const useAddToTrash = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: addToTrash,
    onSuccess: (_, v) => {
      invalidateTrashQueries(queryClient, v.documentType!, [v.documentId!]);
    },
  });
};

export type BulkTrashItem = {
  documentId: string;
  documentType: string;
};

export type BulkAddToTrashResult = {
  succeeded: BulkTrashItem[];
  failed: BulkTrashItem[];
};

async function bulkAddToTrash(
  items: BulkTrashItem[],
): Promise<BulkAddToTrashResult> {
  const results = await Promise.allSettled(
    items.map((item) => addToTrash(item)),
  );

  const succeeded: BulkTrashItem[] = [];
  const failed: BulkTrashItem[] = [];

  results.forEach((result, index) => {
    if (result.status === "fulfilled") {
      succeeded.push(items[index]);
    } else {
      failed.push(items[index]);
    }
  });

  return { succeeded, failed };
}

export const useBulkAddToTrash = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: bulkAddToTrash,
    onSuccess: (result) => {
      if (result.succeeded.length === 0) return;

      const documentType = result.succeeded[0].documentType;
      const documentIds = result.succeeded.map((item) => item.documentId);
      invalidateTrashQueries(queryClient, documentType, documentIds);
    },
  });
};

async function countTrash(where: any) {
  const response = await api.post("/back/api/trash/count", {
    where: where,
  });
  return response.data;
}

export const useCountTrash = (where: any) => {
  return useQuery({
    queryKey: ["trash", "count", where],
    queryFn: () => countTrash(where),
  });
};

async function removeFromTrash(where: any) {
  const response = await api.post("/back/api/trash/removeFromTrash", {
    where: where,
  });
  return response.data;
}

export const useRemoveFromTrash = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: removeFromTrash,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trash"] });
    },
  });
};

async function deleteReelFromEverywhere(where: any) {
  const response = await api.post("/back/api/trash/delete", {
    where: where,
  });
  return response.data;
}

export const useDeleteTotal = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteReelFromEverywhere,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["trash"] });
    },
  });
};
